import { Box, TextField } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { useGetAllPreferences } from '../../services/react-query/preferences';
import { inputContainerStyling, inputDividerStyling } from '../../styles/sxStyling';
import { Contact } from '../../types/Contact';
import { Preference, PREFERENCES_VALID_KEYS } from '../../types/Preference';
import { errorPopulator, uuidFinder } from '../../utility/inputUtils';
import AccountSearchAutocomplete from '../common/AccountSearchAutocomplete/AccountSearchAutocomplete';
import TextFieldWithLink from '../common/TextFieldWithLink/TextFieldWithLink';
import PreferencePicker from '../preferences/picker/PreferencePicker';
import { Account } from '../../types/Account';

interface Props {
  contact: Contact;
  setContact: (contact: Contact) => void;
  disabled?: boolean;
  account?: Account;
}

export default function ContactBasicInfoForm({ contact, setContact, disabled, account }: Props) {
  const updateContactData = (event: React.ChangeEvent<HTMLInputElement>) => {
    setContact({ ...contact, [event.target.id]: event.target.value });
  };

  const { data: preferences = [], isFetching: isGetAllPreferencesFetching } = useGetAllPreferences({
    params: { filters: { keys: [PREFERENCES_VALID_KEYS.CONTACT_TITLE, PREFERENCES_VALID_KEYS.CONTACT_ROLE] } },
  });

  const titleOptions = preferences.filter((p) => p.key === PREFERENCES_VALID_KEYS.CONTACT_TITLE);
  const roleOptions = preferences.filter((p) => p.key === PREFERENCES_VALID_KEYS.CONTACT_ROLE);

  return (
    <Box sx={inputContainerStyling}>
      <Box sx={inputDividerStyling}>
        <AccountSearchAutocomplete
          value={account || null}
          error={errorPopulator(contact, 'account')}
          onChange={(acc) => setContact({ ...contact, account_uuid: acc?.uuid || '' })}
        />

        <TextField
          id="first_name"
          label="First Name"
          value={contact.first_name ?? ''}
          onChange={updateContactData}
          disabled={disabled}
          {...errorPopulator(contact, 'first_name')}
        />

        <TextField
          id="last_name"
          label="Last Name"
          value={contact.last_name ?? ''}
          onChange={updateContactData}
          disabled={disabled}
          {...errorPopulator(contact, 'last_name')}
        />

        <PreferencePicker
          label="Title"
          options={titleOptions}
          loading={isGetAllPreferencesFetching}
          value={uuidFinder(preferences, contact.contact_title_uuid) as Preference | null}
          onValueChange={(pref) => setContact({ ...contact, contact_title_uuid: pref?.uuid })}
          disabled={disabled}
          error={!!errorPopulator(contact, 'title')?.error}
          helperText={errorPopulator(contact, 'title')?.helperText}
        />
      </Box>

      <Box sx={inputDividerStyling}>
        <PreferencePicker
          label="Role"
          options={roleOptions}
          multiple
          loading={isGetAllPreferencesFetching}
          value={_.filter(preferences, (p) => _.includes(contact.roles_uuids, p.uuid))}
          onValueChange={(vals) => setContact({ ...contact, roles_uuids: (vals as Preference[]).map((p) => p.uuid) })}
          disabled={disabled}
          error={!!errorPopulator(contact, 'role')?.error}
          helperText={errorPopulator(contact, 'role')?.helperText}
          fullWidth
        />

        <TextFieldWithLink
          linkType="email"
          id="email"
          label="Email"
          value={contact.email ?? ''}
          onChange={updateContactData}
          disabled={disabled}
          {...errorPopulator(contact, 'email')}
        />

        <Box display="flex" gap={3}>
          <TextFieldWithLink
            linkType="phone"
            id="phone_1"
            label="Phone"
            value={contact.phone_1 ?? ''}
            onChange={updateContactData}
            disabled={disabled}
            {...errorPopulator(contact, 'phone_1')}
            fullWidth
          />

          <TextField
            id="extension"
            label="Extension"
            value={contact.extension ?? ''}
            onChange={updateContactData}
            disabled={disabled}
            {...errorPopulator(contact, 'extension')}
            fullWidth
          />
        </Box>

        <TextField
          id="phone_2"
          label="Mobile"
          value={contact.phone_2 ?? ''}
          onChange={updateContactData}
          disabled={disabled}
          {...errorPopulator(contact, 'phone_2')}
        />
      </Box>
    </Box>
  );
}
